import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Clock, Zap, AlertCircle, CheckCircle, Flame } from 'lucide-react';
import { Button } from './ui/Button';
import { cn } from '../utils/cn';
import pushNotificationService from '../services/pushNotificationService';
import { useAuth } from '../contexts/AuthContext';
import LoadingState from './ui/LoadingState';

export default function NotificationSettings() {
  const { user, profile } = useAuth();
  const [status, setStatus] = useState({ isSubscribed: false, permission: 'default', supported: true });
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [updating, setUpdating] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadNotificationData();
  }, [user]);

  const loadNotificationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentStatus = await pushNotificationService.getSubscriptionStatus();
      setStatus(currentStatus);

      if (currentStatus.isSubscribed) {
        const userSchedules = await pushNotificationService.getNotificationSchedules();
        setSchedules(userSchedules);
      }
    } catch (err) {
      console.error('Error loading notification data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnableNotifications = async () => {
    try {
      setUpdating('enable');
      setError(null);
      
      const success = await pushNotificationService.requestPermission();
      
      if (success) {
        // Enable ALL notification types by default
        const defaultNotifications = [
          { type: 'daily_drill', scheduleTime: '09:00:00', enabled: true },
          { type: 'lumo_encouragement_streak', frequency: 'daily', enabled: true },
          { type: 'lumo_encouragement_milestone', frequency: 'on_milestone', enabled: true },
          { type: 'lumo_encouragement_random', frequency: 'daily', enabled: true },
          { type: 'streak_reminder', scheduleTime: '20:00:00', enabled: true },
          { type: 'lumo_wisdom_wednesday', frequency: 'weekly', enabled: true },
          { type: 'achievement_unlocked', frequency: 'on_milestone', enabled: true }
        ];

        // Create all schedules in parallel
        await Promise.all(
          defaultNotifications.map(notif =>
            pushNotificationService.updateNotificationSchedule(notif.type, {
              scheduleTime: notif.scheduleTime,
              frequency: notif.frequency,
              enabled: notif.enabled
            })
          )
        );
        
        await loadNotificationData();
      }
    } catch (err) {
      console.error('Error enabling notifications:', err);
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleDisableNotifications = async () => {
    try {
      setUpdating('disable');
      setError(null);
      
      await pushNotificationService.disableAllNotifications();
      await loadNotificationData();
    } catch (err) {
      console.error('Error disabling notifications:', err);
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleUpdateSchedule = async (notificationType, updates) => {
    try {
      setUpdating(notificationType);
      setError(null);
      
      const currentSchedule = schedules.find(s => s.notificationType === notificationType);
      await pushNotificationService.updateNotificationSchedule(notificationType, {
        ...currentSchedule,
        ...updates
      });
      
      await loadNotificationData();
    } catch (err) {
      console.error('Error updating schedule:', err);
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return <LoadingState message="Loading notification settings..." />;
  }

  if (!status.supported) {
    return (
      <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-foreground mb-1">Push Notifications Not Supported</h3>
            <p className="text-sm text-muted-foreground">
              Your browser doesn't support push notifications. Try using Chrome, Edge, or Safari on iOS 16.4+.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <Bell className="w-5 h-5 text-primary-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground">Push Notifications</h3>
            <p className="text-sm text-muted-foreground">Stay on track with daily reminders</p>
          </div>
          {status.isSubscribed && (
            <div className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">Active</span>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-destructive-50 border border-destructive-200 rounded-lg">
            <p className="text-sm text-destructive-700">{error}</p>
          </div>
        )}

        {status.permission === 'denied' && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900 mb-1">Notifications Blocked</p>
                <p className="text-sm text-amber-700">
                  Please enable notifications in your browser settings to receive reminders from Lumo.
                </p>
              </div>
            </div>
          </div>
        )}

        {!status.isSubscribed && status.permission !== 'denied' && (
          <div className="p-6 bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-primary-900 mb-2">Never miss your daily drill</h4>
                <p className="text-sm text-primary-700 mb-4">
                  Get reminders from Lumo to keep your streak alive and stay sharp. We'll send you:
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2 text-sm text-primary-700">
                    <Clock className="w-4 h-4" />
                    Daily drill reminders at your chosen time
                  </li>
                  <li className="flex items-center gap-2 text-sm text-primary-700">
                    <Zap className="w-4 h-4" />
                    Motivational check-ins from Lumo
                  </li>
                  <li className="flex items-center gap-2 text-sm text-primary-700">
                    <Flame className="w-4 h-4" />
                    Streak alerts (don't let it break!)
                  </li>
                </ul>
                <Button 
                  onClick={handleEnableNotifications}
                  disabled={updating === 'enable'}
                  className="w-full sm:w-auto"
                >
                  {updating === 'enable' ? 'Enabling...' : 'Enable Notifications'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notification Schedules */}
      {status.isSubscribed && schedules.length > 0 && (
        <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-sm">
          <h4 className="font-semibold text-foreground mb-4">Notification Preferences</h4>
          
          <div className="space-y-4">
            {schedules.map((schedule) => (
              <NotificationScheduleItem
                key={schedule.id}
                schedule={schedule}
                onUpdate={handleUpdateSchedule}
                updating={updating === schedule.notificationType}
              />
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <Button
              variant="outline"
              onClick={handleDisableNotifications}
              disabled={updating === 'disable'}
              className="w-full"
            >
              <BellOff className="w-4 h-4 mr-2" />
              {updating === 'disable' ? 'Disabling...' : 'Disable All Notifications'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Sub-component for individual notification schedule
function NotificationScheduleItem({ schedule, onUpdate, updating }) {
  const getIcon = () => {
    switch (schedule.notificationType) {
      case 'daily_drill': return Clock;
      case 'lumo_encouragement_streak': return Zap;
      case 'streak_reminder': return Flame;
      default: return Bell;
    }
  };

  const getLabel = () => {
    switch (schedule.notificationType) {
      case 'daily_drill': return 'Daily Drill Reminder';
      case 'lumo_encouragement_streak': return 'Lumo Encouragement';
      case 'streak_reminder': return 'Streak Saver';
      default: return schedule.notificationType;
    }
  };

  const getDescription = () => {
    switch (schedule.notificationType) {
      case 'daily_drill': return 'Get notified to complete your daily practice';
      case 'lumo_encouragement_streak': return 'Motivational messages from your coach';
      case 'streak_reminder': return 'Alert if you\'re about to break your streak';
      default: return '';
    }
  };

  const Icon = getIcon();

  return (
    <div className="flex items-start justify-between p-4 bg-muted/30 rounded-lg">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-4 h-4 text-primary-600" />
          <span className="font-medium text-foreground">{getLabel()}</span>
        </div>
        <p className="text-sm text-muted-foreground mb-2">{getDescription()}</p>
        
        {schedule.scheduleTime && (
          <div className="mt-2">
            <label className="text-xs text-muted-foreground block mb-1">Notification time</label>
            <input
              type="time"
              value={schedule.scheduleTime?.substring(0, 5) || '09:00'}
              onChange={(e) => onUpdate(schedule.notificationType, { scheduleTime: e.target.value + ':00' })}
              disabled={!schedule.enabled || updating}
              className={cn(
                "text-sm border border-input rounded px-2 py-1 bg-background",
                (!schedule.enabled || updating) && "opacity-50 cursor-not-allowed"
              )}
            />
          </div>
        )}

        {schedule.frequency && (
          <div className="mt-2">
            <select
              value={schedule.frequency}
              onChange={(e) => onUpdate(schedule.notificationType, { frequency: e.target.value })}
              disabled={!schedule.enabled || updating}
              className={cn(
                "text-sm border border-input rounded px-2 py-1 bg-background",
                (!schedule.enabled || updating) && "opacity-50 cursor-not-allowed"
              )}
            >
              <option value="daily">Daily</option>
              <option value="on_streak">On streak milestones</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
        )}
      </div>
      
      <label className="relative inline-flex items-center cursor-pointer ml-4">
        <input
          type="checkbox"
          checked={schedule.enabled}
          onChange={(e) => onUpdate(schedule.notificationType, { enabled: e.target.checked })}
          disabled={updating}
          className="sr-only peer"
        />
        <div className={cn(
          "w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer",
          "peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px]",
          "after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all",
          "peer-checked:bg-primary-600",
          updating && "opacity-50 cursor-not-allowed"
        )} />
      </label>
    </div>
  );
}
